import { FormEvent, useCallback, useState } from 'react'

import { ERROR_MESSAGES } from '../../../utils/errorMessages'
import { MIN_SIZE, validatePasswordRules } from '../../../utils/passwordStrength'

export interface ChangePasswordSubmitInput {
  currentPassword: string
  newPassword: string
}

interface UseChangePasswordFormOptions {
  onValidSubmit: (input: ChangePasswordSubmitInput) => Promise<void> | void
  fullName?: string
  email?: string
}

interface FormErrors {
  currentPassword?: string
  newPassword?: string
  confirmPassword?: string
}

interface FormTouched {
  currentPassword: boolean
  newPassword: boolean
  confirmPassword: boolean
}

const INITIAL_TOUCHED: FormTouched = {
  currentPassword: false,
  newPassword: false,
  confirmPassword: false,
}

function validateCurrent(value: string): string | undefined {
  if (!value) return ERROR_MESSAGES.changePassword.currentPassword.empty
  return undefined
}

function validateNew(
  value: string,
  currentPassword: string,
  fullName?: string,
  email?: string
): string | undefined {
  const rulesError = validatePasswordRules(value, MIN_SIZE, fullName, email).errorMessage

  if (rulesError) return rulesError

  if (value && value === currentPassword)
    return ERROR_MESSAGES.changePassword.newPassword.sameAsCurrent

  return undefined
}

function validateConfirm(value: string, newValue: string): string | undefined {
  if (value !== newValue) return ERROR_MESSAGES.changePassword.confirmPassword.mismatch
  return undefined
}

export function useChangePasswordForm({
  onValidSubmit,
  fullName,
  email,
}: UseChangePasswordFormOptions) {
  const [currentPassword, setCurrentPassword] = useState<string>('')
  const [newPassword, setNewPassword] = useState<string>('')
  const [confirmPassword, setConfirmPassword] = useState<string>('')
  const [touched, setTouched] = useState<FormTouched>(INITIAL_TOUCHED)
  const [errors, setErrors] = useState<FormErrors>({})

  const handleCurrentBlur = useCallback(() => {
    setTouched((prev) => ({ ...prev, currentPassword: true }))
    setErrors((prev) => ({
      ...prev,
      currentPassword: validateCurrent(currentPassword),
      // Re-validate the new password too in case the user filled it before the
      // current one — `sameAsCurrent` depends on both values.
      ...(prev.newPassword !== undefined || newPassword
        ? { newPassword: validateNew(newPassword, currentPassword, fullName, email) }
        : null),
    }))
  }, [currentPassword, newPassword, fullName, email])

  const handleNewBlur = useCallback(() => {
    setTouched((prev) => ({ ...prev, newPassword: true }))
    setErrors((prev) => ({
      ...prev,
      newPassword: validateNew(newPassword, currentPassword, fullName, email),
    }))
  }, [newPassword, currentPassword, fullName, email])

  const handleConfirmBlur = useCallback(() => {
    setTouched((prev) => ({ ...prev, confirmPassword: true }))
    setErrors((prev) => ({
      ...prev,
      confirmPassword: validateConfirm(confirmPassword, newPassword),
    }))
  }, [confirmPassword, newPassword])

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    const nextErrors: FormErrors = {
      currentPassword: validateCurrent(currentPassword),
      newPassword: validateNew(newPassword, currentPassword, fullName, email),
      confirmPassword: validateConfirm(confirmPassword, newPassword),
    }

    setTouched({ currentPassword: true, newPassword: true, confirmPassword: true })
    setErrors(nextErrors)

    if (nextErrors.currentPassword || nextErrors.newPassword || nextErrors.confirmPassword) return

    await onValidSubmit({ currentPassword, newPassword })
  }

  return {
    currentPassword: {
      value: currentPassword,
      error: touched.currentPassword ? errors.currentPassword : undefined,
      onChange: setCurrentPassword,
      onBlur: handleCurrentBlur,
    },
    newPassword: {
      value: newPassword,
      error: touched.newPassword ? errors.newPassword : undefined,
      onChange: setNewPassword,
      onBlur: handleNewBlur,
    },
    confirmPassword: {
      value: confirmPassword,
      error: touched.confirmPassword ? errors.confirmPassword : undefined,
      onChange: setConfirmPassword,
      onBlur: handleConfirmBlur,
    },
    handleSubmit,
  }
}
